import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../theme';
import { Card, CardHeader, CardTitle, CardBody } from '../../ui/Card';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { useAuth } from '../../../contexts/AuthContext';
import { loadAllGuardsForExport, GuardData } from '../../../pages/manage_guard/service/guardService';

const ChartCard = styled(Card)`
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03) !important;
  border: 1px solid ${theme.colors.light.border.light};
  
  &:hover {
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.05) !important;
  }
`;

const ChartContainer = styled.div`
  width: 100%;
  height: 450px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
`;

const Description = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.light.text.secondary};
  margin-bottom: ${theme.spacing[8]};
  line-height: 1.7;
  max-width: 800px;
`;

const ChartWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[10]};
  width: 100%;
  justify-content: center;

  @media (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${theme.spacing[6]};
  }
`;

const PieWrapper = styled.div`
  flex: 0 0 auto;
  max-width: 350px;
  width: 100%;
`;

const LegendWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
  padding: ${theme.spacing[4]};
  background: ${theme.colors.light.background.tertiary};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.light.border.light};
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[2]};
  border-radius: ${theme.borderRadius.md};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.white};
  }
`;

const LegendColor = styled.div<{ color: string }>`
  width: 16px;
  height: 16px;
  border-radius: ${theme.borderRadius.full};
  background-color: ${({ color }) => color};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
`;

const LegendText = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.light.text.primary};
  font-weight: ${theme.typography.fontWeight.medium};
`;

const CustomerSatisfaction: React.FC = () => {
  const { companyData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<Array<{ name: string; value: number; color: string; count: number }>>([]);

  useEffect(() => {
    const fetchDeploymentData = async () => {
      if (!companyData?.id) {
        setLoading(false);
        return;
      }

      try {
        const guards = await loadAllGuardsForExport(companyData.id);
        
        // Count deployments by client type
        const deploymentCounts: Record<string, number> = {
          Bank: 0,
          Factory: 0,
          Individual: 0,
        };

        guards.forEach((guard) => {
          const deployment = guard.currentDeployment;
          if (deployment?.client) {
            const clientType = deployment.client.type;
            if (clientType === 'Bank' || clientType === 'Factory' || clientType === 'Individual') {
              deploymentCounts[clientType]++;
            }
          }
        });

        const total = Object.values(deploymentCounts).reduce((sum, count) => sum + count, 0);
        
        const chartData = [
          {
            name: 'Bank',
            value: total > 0 ? (deploymentCounts.Bank / total) * 100 : 0,
            color: theme.colors.primary[700],
            count: deploymentCounts.Bank,
          },
          {
            name: 'Factory',
            value: total > 0 ? (deploymentCounts.Factory / total) * 100 : 0,
            color: theme.colors.primary[500],
            count: deploymentCounts.Factory,
          },
          {
            name: 'Individual',
            value: total > 0 ? (deploymentCounts.Individual / total) * 100 : 0,
            color: theme.colors.primary[300],
            count: deploymentCounts.Individual,
          },
        ].filter((item) => item.count > 0); // Only show types with deployments

        setData(chartData);
      } catch (error) {
        console.error('Error fetching deployment data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDeploymentData();
  }, [companyData]);

  const COLORS = data.map((item) => item.color);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          style={{
            backgroundColor: theme.colors.white,
            padding: theme.spacing[3],
            border: `1px solid ${theme.colors.light.border.medium}`,
            borderRadius: theme.borderRadius.md,
            boxShadow: theme.shadows.lg,
          }}
        >
          <p style={{ margin: 0, color: theme.colors.light.text.primary, fontWeight: theme.typography.fontWeight.semibold }}>
            {data.name}: {data.count} guards ({data.value.toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <ChartCard>
        <CardBody>
          <div style={{ textAlign: 'center', padding: theme.spacing[8] }}>Loading chart...</div>
        </CardBody>
      </ChartCard>
    );
  }

  if (data.length === 0) {
    return (
      <ChartCard>
        <CardBody>
          <CardTitle>Deployment by Client Type</CardTitle>
          <Description>No deployment data available.</Description>
        </CardBody>
      </ChartCard>
    );
  }

  return (
    <ChartCard>
      <CardBody>
        <CardTitle>Deployment by Client Type</CardTitle>
        <Description>
          Distribution of guard deployments across different client types (Bank, Factory, Individual).
        </Description>
        <ChartContainer>
          <ChartWrapper>
            <PieWrapper>
              <ResponsiveContainer width="100%" height={350}>
                <PieChart>
                  <defs>
                    <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                      <feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.1"/>
                    </filter>
                  </defs>
                  <Pie
                    data={data}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ percent, cx, cy, midAngle, innerRadius, outerRadius }) => {
                      if (!percent || midAngle === undefined || cx === undefined || cy === undefined || innerRadius === undefined || outerRadius === undefined) return null;
                      const RADIAN = Math.PI / 180;
                      const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                      const x = cx + radius * Math.cos(-midAngle * RADIAN);
                      const y = cy + radius * Math.sin(-midAngle * RADIAN);
                      
                      return (
                        <text
                          x={x}
                          y={y}
                          fill={theme.colors.light.text.primary}
                          textAnchor={x > cx ? 'start' : 'end'}
                          dominantBaseline="central"
                          fontSize={13}
                          fontWeight={theme.typography.fontWeight.bold}
                          filter="url(#shadow)"
                        >
                          {`${(percent * 100).toFixed(1)}%`}
                        </text>
                      );
                    }}
                    outerRadius={120}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {data.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </PieWrapper>
            <LegendWrapper>
              {data.map((item, index) => (
                <LegendItem key={index}>
                  <LegendColor color={item.color} />
                  <LegendText>
                    {item.name}: {item.count} guards ({item.value.toFixed(1)}%)
                  </LegendText>
                </LegendItem>
              ))}
            </LegendWrapper>
          </ChartWrapper>
        </ChartContainer>
      </CardBody>
    </ChartCard>
  );
};

export default CustomerSatisfaction;

